import { useState, useEffect, useMemo } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, Tooltip,
  DialogTitle, Divider, FormControl, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, TextField
} from "@mui/material";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import api from "../../api";
import { useDebounce } from "../../hooks/useDebounce";
import {
  SnackBarComponent, getComparator, EnhancedTableHead, EnhancedTableToolbar,
  TableLoadingState, DeleteConfirmDialog, ImagePreviewCell, ExternalLinkCell,
  StatusChip, StatusToggleSwitch, ImageUploadSection
} from "../../components";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);


const headCells = [
  { id: "title", numeric: false, disablePadding: false, label: "Title" },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: "Description",
  },
  {
    id: "desktopimage",
    numeric: false,
    disablePadding: false,
    label: "Desktop Image",
  },
  {
    id: "mobileimage",
    numeric: false,
    disablePadding: false,
    label: "Mobile Image",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  /*{
    id: "action",
    numeric: false,
    disablePadding: false,
    label: "",
  },*/
];



export default function Awards() {
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("title");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [desktopImg, setDesktopImg] = useState([]);
  const [mobileImg, setMobileImg] = useState([]);
  // snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // onChange={(date) => setStartDate(date)}
  // const [publishedOn, setPublishedOn] = React.useState();
  // dialog box
  const [open, setOpen] = useState(false);
  const [scroll, setScroll] = useState("paper");
  const [isLoading, setIsLoading] = useState(true); // Initial loading state
  const [tags, setTags] = useState([]);

  const [newData, setNewData] = useState({
    title: "",
    description: "",
    desktopImage: "",
    mobileImage: "",
    desktopImageUrl: "",
    mobileImageUrl: "",
    status: "",
  });
  // update | edit
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // State for Delete Confirmation Dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleInputChange = (e) => {
    setNewData({ ...newData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date) => {
    setNewData((prevState) => ({
      ...prevState,
      publishedOn: date ? date.format("LL") : "",
    }));
  };

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
    setIsEditing(false);
    setNewData({
      title: "",
      description: "",
      desktopImage: "",
      mobileImage: "",
      desktopImageUrl: "",
      mobileImageUrl: "",
      status: "Hidden",
    });
    setDesktopImg([]);
    setMobileImg([]);
  };

  const handleEditClick = () => {
    const selectedRow = rows.find((row) => row.id === selected[0]);
    const hasImage = selectedRow.image;
    setNewData({
      title: selectedRow.title,
      description: selectedRow.description,
      status: selectedRow.status,
      desktopImageUrl: hasImage ? selectedRow.image.desktop || "" : "",
      mobileImageUrl: hasImage ? selectedRow.image.mobile || "" : "",
    });

    setDesktopImg(hasImage && selectedRow.image.desktop ? [
      {
        source: selectedRow.image.desktop, // Existing desktop image URL for preview
        options: {
          type: 'url',
        },
      },
    ] : []);
    setMobileImg(hasImage && selectedRow.image.mobile ? [
      {
        source: selectedRow.image.mobile, // Existing mobile image URL for preview
        options: {
          type: 'url',
        },
      },
    ] : []);
    setEditingId(selectedRow.id);
    setIsEditing(true); // Open in edit mode
    setOpen(true);
    // Optionally, handle image files if needed
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      // Validate form fields (add more validation as needed)
      if (
        !newData.title ||
        !newData.description
      ) {
        setSnackbarMessage("Please fill all required fields!");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
        return;
      }
      // Validate images
      if (
        (!desktopImg || desktopImg.length === 0 || (isEditing ? false : !desktopImg[0].file)) &&
        !newData.desktopImageUrl
      ) {
        setSnackbarMessage("Please provide a desktop image (URL or upload)!");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
        return;
      }

      // Create a FormData object to send files and form data together
      const formData = new FormData();

      // Append text data
      formData.append("title", newData.title);
      formData.append("description", newData.description);
      if (isEditing) {
        formData.append("status", newData.status);
      }
      else {
        formData.append("status", "Hidden");
      }
      if (newData.desktopImageUrl !== undefined && newData.desktopImageUrl !== null) {
        formData.append("desktopImageUrl", newData.desktopImageUrl);
      }
      if (newData.mobileImageUrl !== undefined && newData.mobileImageUrl !== null) {
        formData.append("mobileImageUrl", newData.mobileImageUrl);
      }
      // Append images if they exist
      if (desktopImg.length > 0 && desktopImg[0].file && typeof desktopImg[0].source !== "string") {
        // setDesktopImg()
        formData.append("desktopImage", desktopImg[0].file);
      }
      if (mobileImg.length > 0 && mobileImg[0].file && typeof mobileImg[0].source !== "string") {
        formData.append("mobileImage", mobileImg[0].file);
      }
      if (isEditing && editingId) {
        const [country_code, created_at] = editingId.split('#');
        formData.append("country_code", country_code);
        formData.append("created_at", created_at);
      } else {
        formData.append("country_code", "IN");
      }
      const token = localStorage.getItem("accessToken");
      // Decide whether to create or update data
      if (isEditing) {
        // Update existing data
        await api.put(
          `/api/awards`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Set the Authorization header
            },
          }
          //`http://localhost:7777/api/awards/${editingId}`,{status:"published"},
          // {
          //   headers: { "Content-Type": "multipart/form-data" },
          // }
        );

        // Show success snackbar
        setSnackbarMessage("Changes saved successfully!");
        setSnackbarSeverity("success");
      } else {
        // Create new data
        await api.post("/api/awards", formData, {
          headers: {
            Authorization: `Bearer ${token}`, // Set the Authorization header
          },
          //headers: { "Content-Type": "multipart/form-data" },
        });

        // Show success snackbar
        setSnackbarMessage("News added successfully!");
        setSnackbarSeverity("success");
      }

      setOpenSnackbar(true);

      // Refresh the table data after submission or edit
      fetchData();

      // Reset form after submission
      setOpen(false); // Close the dialog box
      setSelected([]); // Clear selected items
      setNewData({
        title: "",
        description: "",
      }); // Reset input fields
      setDesktopImg([]); // Clear desktop image
      setMobileImg([]); // Clear mobile image
    } catch (error) {
      console.error(
        "Error creating/editing data:",
        error.response?.data || error
      );

      // Show error snackbar
      setSnackbarMessage(error.response.data.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("accessToken");
    let allItems = [];
    let nextPageKey = null;

    try {
      do {
        const response = await api.get("/api/awards", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            limit: 50,
            pageKey: nextPageKey ? encodeURIComponent(JSON.stringify(nextPageKey)) : undefined,
          },
        });

        const data = response.data.items || response.data; // Handle both paginated and non-paginated responses just in case
        if (data && data.length > 0) {
          const mappedData = data.map(item => ({ ...item, id: `${item.country_code}#${item.created_at}` }));
          allItems = [...allItems, ...mappedData];
          setRows([...allItems]); // Update state iteratively for instant feedback
        }

        // Extract nextPageKey if available from backend
        nextPageKey = response.data.nextPageKey ? JSON.parse(decodeURIComponent(response.data.nextPageKey)) : null;

      } while (nextPageKey);

      setRows(allItems);
      setIsLoading(false); // Stop loading spinner
    } catch (error) {
      console.error("Error fetching data:", error);
      const { status, data } = error.response || {};
      const message = data ? data.message : "Error fetching data";
      setErrorMessage(message);
      setIsLoading(false);
    }
  };

  const fetchTags = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await api.get("/api/tags", {
        headers: {
          Authorization: `Bearer ${token}`, // Set the Authorization header
        },
      });
      // Check if the response status is 200 or 201 and data is empty
      setTags(response.data.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      const { status, data } = error.response;
      const { message } = data;
      setErrorMessage(message);
    }
  };

  // More option code
  const ITEM_HEIGHT = 48;

  const [anchorEl, setAnchorEl] = useState(null);
  const openMore = Boolean(anchorEl);
  const handleMoreClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    fetchData();
    fetchTags();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when searching
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  // Handle Delete Confirmation Dialog
  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      await Promise.all(
        selected.map((id) => {
          const [country_code, created_at] = id.split('#');
          return api.delete(`/api/awards`, {
            headers: {
              Authorization: `Bearer ${token}`, // Set the Authorization header
            },
            data: { country_code, created_at: Number(created_at) }
          });
        })
      );
      fetchData();
      setSelected([]);
      setOpenDeleteDialog(false);

      // Show success snackbar
      setSnackbarMessage("Deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true); // Open snackbar
    } catch (error) {
      console.error("Error deleting data:", error);

      // Show error snackbar
      setSnackbarMessage(error.response.data.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true); // Open snackbar
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false); // Reset the state to close the snackbar
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };
  const [tagError, setTagError] = useState(true);

  const filteredRows = useMemo(() => {
    if (!debouncedSearchQuery) return rows;
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return rows.filter((row) => {
      const titleMatch = (row.title?.toLowerCase() || "").includes(lowerQuery);
      const descMatch = (row.description?.toLowerCase() || "").includes(lowerQuery);
      return titleMatch || descMatch;
    });
  }, [rows, debouncedSearchQuery]);

  const visibleRows = useMemo(
    () =>
      [...filteredRows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Snack bar */}
      {openSnackbar && (
        <SnackBarComponent
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleSnackbarClose} // Handle the snackbar close
        />
      )}

      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar
          title="AWARDS"
          numSelected={selected.length}
          handleClickOpen={handleClickOpen}
          handleEditClick={handleEditClick}
          handleDeleteClick={handleDeleteClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              headCells={headCells}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={filteredRows.length}
            />
            <TableBody>
              {(isLoading || visibleRows.length === 0) ? (
                <TableLoadingState
                  isLoading={isLoading}
                  errorMessage={errorMessage}
                  colSpan={10}
                />
              ) : (
                visibleRows.map((row, index) => {
                  const isItemSelected = selected.includes(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  {
                    /* {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .sort(getComparator(order, orderBy))
                .map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`; */
                  }

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.title}
                      </TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>
                        {/* Display the image if available */}
                        {row.image && row.image.desktop ? (
                          <ImagePreviewCell imageUrl={row.image.desktop} alt="Desktop Image" />
                        ) : (
                          "No Image"
                        )}
                      </TableCell>
                      <TableCell>
                        {row.image && row.image.mobile ? (
                          <ImagePreviewCell imageUrl={row.image.mobile} alt="Mobile Image" />
                        ) : (
                          "No Image"
                        )}
                      </TableCell>

                      <TableCell>
                        {/* {row.status} */}
                        <Chip
                          label={
                            row.status === "published"
                              ? "visible"
                              : "not visible"
                          }
                          color={
                            row.status === "published" ? "success" : "warning"
                          }
                          variant="contained"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        scroll={scroll}
        aria-labelledby="dialog-title"
        fullWidth
      >
        <DialogTitle id="dialog-title">
          {isEditing ? "Edit" : "Add"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{ "& > :not(style)": { mt: 1.5, mb: 1.5, width: "100%" } }}
            noValidate
            autoComplete="off"
          >
            <TextField
              //error
              // error={newData.title === "" ? true : false}
              margin="dense"
              name="title"
              label="Title"
              type="text"
              fullWidth
              value={newData.title}
              onChange={handleInputChange}
            />
            <TextField
              //error
              // error={newData.description === "" ? true : false}
              margin="dense"
              name="description"
              label="Description"
              type="text"
              multiline
              rows={3}
              fullWidth
              value={newData.description}
              onChange={handleInputChange}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <StatusToggleSwitch
                isEditing={isEditing}
                status={newData.status}
                onChange={(val) => setNewData({ ...newData, status: val })}
              />
            </Box>
          </Box>
          <Divider sx={{ '& > :not(style)': { mt: 1.5, mb: 1.5, width: '100%' } }}>
            <Chip label="Upload Images" size="small" />
          </Divider>
          <ImageUploadSection
            desktopImageUrl={newData.desktopImageUrl}
            mobileImageUrl={newData.mobileImageUrl}
            desktopImg={desktopImg}
            mobileImg={mobileImg}
            onChangeDesktopUrl={handleInputChange}
            onChangeMobileUrl={handleInputChange}
            onUpdateDesktopFiles={setDesktopImg}
            onUpdateMobileFiles={setMobileImg}
          />
        </DialogContent>
        <DialogActions sx={{ "& > :not(style)": { m: 1.5, width: "100%" } }}>
          <Button variant="contained" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isEditing ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={openDeleteDialog}
        selectedCount={selected.length}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}